import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Box,
  Button,
  Typography,
  CircularProgress
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DownloadIcon from '@mui/icons-material/Download';
import JSZip from 'jszip';

const ImageDialog = ({
  open,
  onClose,
  images,
  selectedIndex,
  onChangeImage,
  product
}) => {
  const [isDownloading, setIsDownloading] = useState(false);

  if (!open || !images || images.length === 0 || selectedIndex === null) return null;

  const currentImage = images[selectedIndex];

  const handlePrevImage = () => {
    onChangeImage(selectedIndex > 0 ? selectedIndex - 1 : images.length - 1);
  };

  const handleNextImage = () => {
    onChangeImage(selectedIndex < images.length - 1 ? selectedIndex + 1 : 0);
  };

  // Generate appropriate file name based on product type
  const generateFileName = (product, index) => {
    if (!product) return `image-${index + 1}.jpg`;
    
    if (product.category === 'watch') {
      return `${product.brand || ''} ${product.model || ''} ${index + 1}.jpg`.trim();
    } else {
      return `${product.id || ''}_${index + 1}.jpg`.trim();
    }
  };

  // Download a single image
  const handleDownloadImage = async () => {
    try {
      const response = await fetch(currentImage.url);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = generateFileName(product, selectedIndex);
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error downloading image:", error);
    }
  };

  // Download all images as ZIP
  const handleDownloadAllImages = async () => {
    setIsDownloading(true);
    try {
      const zip = new JSZip();
      
      const downloads = images.map(async (image, index) => {
        try {
          const response = await fetch(image.url);
          const blob = await response.blob();
          zip.file(generateFileName(product, index), blob);
          return true;
        } catch (error) {
          console.error(`Error downloading image ${index}:`, error);
          return false;
        }
      });
      
      await Promise.all(downloads);
      
      const content = await zip.generateAsync({ type: 'blob' });
      const url = window.URL.createObjectURL(content);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${product?.id || 'product'}-images.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error creating ZIP:", error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Zdjęcie {selectedIndex + 1} z {images.length}</Typography>
          <Box>
            <Button
              startIcon={<DownloadIcon />}
              onClick={handleDownloadImage}
              sx={{ mr: 1 }}
            >
              Pobierz zdjęcie
            </Button>
            <Button
              startIcon={isDownloading ? <CircularProgress size={20} /> : <DownloadIcon />}
              onClick={handleDownloadAllImages}
              disabled={isDownloading}
              sx={{ mr: 1 }}
            >
              Pobierz wszystkie
            </Button>
            <IconButton
              edge="end"
              color="inherit"
              onClick={onClose}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ position: 'relative', width: '100%', textAlign: 'center' }}>
          <IconButton
            onClick={handlePrevImage}
            sx={{
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 1,
              backgroundColor: 'rgba(255,255,255,0.5)',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.8)' },
            }}
          >
            <ChevronLeftIcon />
          </IconButton>
          
          <Box 
            sx={{ 
              height: 'calc(80vh - 150px)',
              backgroundColor: '#000000', // Czarne tło pod zdjęciem
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%'
            }}
          >
            <img
              src={currentImage.url}
              alt={`Zdjęcie ${selectedIndex + 1}`}
              style={{
                maxHeight: '100%',
                maxWidth: '100%',
                objectFit: 'contain'
              }}
            />
          </Box>
          
          <IconButton
            onClick={handleNextImage}
            sx={{
              position: 'absolute',
              right: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 1,
              backgroundColor: 'rgba(255,255,255,0.5)',
              '&:hover': { backgroundColor: 'rgba(255,255,255,0.8)' },
            }}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ImageDialog;
